import { useState } from "react";
import { FlowPrimaryButton } from "@/components/consultation-flow/ui";
import type { FreeTextNode as FreeTextNodeType } from "@/lib/pharmacyFirst/types";

export interface FreeTextAnswer {
  text: string;
}

interface FreeTextNodeProps {
  node: FreeTextNodeType;
  isDarkMode?: boolean;
  stepNumber?: number;
  onAnswer: (answer: FreeTextAnswer) => void;
}

export function FreeTextNode({
  node,
  isDarkMode = false,
  stepNumber,
  onAnswer,
}: FreeTextNodeProps) {
  const [text, setText] = useState("");

  const trimmed = text.trim();
  const minLength = node.minLength ?? 0;
  const valid = node.required === false ? true : trimmed.length > 0 && trimmed.length >= minLength;

  const handleContinue = () => {
    if (!valid) return;
    onAnswer({ text: trimmed });
  };

  const cardClass = `border rounded-2xl p-4 ${
    isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
  }`;

  return (
    <div className={cardClass}>
      <div className="flex items-center gap-3 mb-3">
        {stepNumber !== undefined && (
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold flex-shrink-0 ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
        )}
        <div>
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            {node.text}
          </h3>
          {node.hint && (
            <p className={`text-xs mt-1 ${isDarkMode ? "text-gray-400" : "text-gray-600"}`}>{node.hint}</p>
          )}
        </div>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={4}
        aria-label={node.text}
        className={`w-full rounded-lg border-2 px-3 py-2.5 text-sm focus:outline-none ${
          isDarkMode
            ? "bg-gray-700 text-white border-gray-600 placeholder-gray-500"
            : "bg-white text-gray-900 border-gray-300 placeholder-gray-400"
        }`}
        placeholder="Enter notes…"
      />

      <FlowPrimaryButton
        label="Continue"
        className="w-full mt-3"
        onClick={handleContinue}
        disabled={!valid}
        blockedReason="Enter your notes to continue."
      />
    </div>
  );
}
